import {
  Typography,
  LinearProgress,
  Alert,
  FormControl,
  Chip,
  Button,
  TextField,
  Box,
  Checkbox,
  DialogTitle,
  IconButton,
} from '@mui/material';
import axios, { AxiosResponse } from 'axios';
import { useState, useContext, useEffect } from 'react';
import { isDesktop, isMobile, isTablet } from 'react-device-detect';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { emptyDeliveryBatch } from '../../../data/deliverytasks';
import { Workflow, DeliveryState } from '../../../enums/orders';
import { DriverType } from '../../../interfaces/delivery-company/drivers';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { IDeliveryBatch, IOrderDeliveryTask } from '../../../interfaces/delivery-company/tasks';
import { displayLocation } from '../../../utils/locations';
import AutocompleteWithThrottle from '../../common/autocomplete-with-throttle/autocomplete-with-throttle';
import { ErrorsFlattenner } from '../../common/errors-flattenner/errors-flattenner';
import { GenericTableProps } from '../../common/generic-table/utils';
import MultiPackageScanner, {
  timestampedMessage,
  validationResult,
} from '../../common/multi-package-scanner/multi-package-scanner';
import { showDeliveryState } from '../../common/styles/order-states.style';
import {
  Decoding,
  DecodingWithOrderVal,
  generateUID,
} from '../../../utils/encoding';
import { getMissingPackages } from '../../../utils/missing-packages';
import { values } from 'lodash';
import CustomDialog from '../../common/custom-dialog/custom-dialog';
import MarkAsFailedDialog from '../../driver-components/common/mark-as-failed-dialog';
import SetFailureReason from './update-failure-reason';
import { showWorkflow } from '../../common/styles/workflow-display';
import { get_cod } from '../../../utils/cod';
import { displayMoney, minus, Money } from '../../../utils/money';
import UpdateCOD from './update-cod';
import { ClearIcon } from '@mui/x-date-pickers';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { DownloadIcon } from 'lucide-react';
import { PrintWaybillsMultiSizes } from '../../common/bulk-action/print-waybills';
import UpdateCommissionDialog from '../../common/delivery-task-commission/update-commission-dialog';
import { OrderDeliveryTaskState } from '../../../enums/tasks';
import { user_is_admin } from '../../../utils/permissions';

function FailedDeliveriesReceiver() {
  const [driver, setDriver] = useState<DriverType | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [response, setResponse] = useState<AxiosResponse>();
  const [scannedOrders, setScannedOrders] = useState<DecodingWithOrderVal[]>(
    []
  );
  const [failureReasonOverrides, setFailureReasonOverrides] = useState<
    Map<string, string>
  >(new Map());
  const [rtoOrdersOverrides, setRtoOrdersOverrides] = useState<
    Map<string, boolean>
  >(new Map());
  const [codOverrides, setCODOverrides] = useState<Map<string, Money[]>>(
    new Map()
  );
  const [commissionOverrides, setCommissionOverrides] = useState<Map<string, Money[]>>(
    new Map()
  );
  const [updateCommissionFor, setUpdateCommissionFor] = useState<OrderType | null>(null);

  const [updateFailureReasonFor, setUpdateFailureReasonFor] =
    useState<OrderType | null>(null);
  const [updateCODFor, setUpdateCODFor] = useState<OrderType | null>(null);
  const [printingWaybillsForRTO, setPrintingWaybillsForRTO] =
    useState<boolean>(false);

  const { user, config } = useContext(AuthContext);

  const handleSubmit = async (
    decodingsWithVal: DecodingWithOrderVal[],
    setIsLoading: (isLoading: boolean) => void,
    setResponse: (resp?: AxiosResponse) => void,
    setMessage: (m: string) => void,
    config: any
  ) => {
    setIsLoading(true);
    setResponse(undefined);
    setLoadingMessage('Confirming drop at warehouse...');
    if (decodingsWithVal.some((item) => item.type !== 'permanent')) {
      setMessage(
        'You can not receive failed deliveries with temporary barcodes'
      );
      setIsLoading(false);
      setLoadingMessage('');
      return;
    }

    const missingPackages = getMissingPackages(decodingsWithVal);
    if (missingPackages.length > 0) {
      setIsLoading(false);
      setMessage(
        `The following packages are missing: ${missingPackages.map((item) => `package ${item[1]} from ${item[0]}`).join(', ')}`
      );
      return;
    }

    let requestData: any[] = Array.from(decodingsWithVal.values())
      .map((item) => item.order)
      .map((order) => {
        return {
          id: order.id,
          delivery_state: DeliveryState.AT_WAREHOUSE,
          reason: 'Received at warehouse.',
          return_reason: order.last_delivery_task?.failure_reason,
          price: order.price,
          delivery_fee: order.delivery_fee,
          failed_delivery_commission: commissionOverrides.get(order.id) ?? order.last_delivery_task?.failed_delivery_commission
        };
      });

    requestData = requestData.map((item) => {
      const overridden = item;
      if (rtoOrdersOverrides.has(item.id)) {
        overridden.workflow = rtoOrdersOverrides.get(item.id)
          ? Workflow.RETURN
          : Workflow.STANDARD;
      }

      if (failureReasonOverrides.has(item.id)) {
        overridden.return_reason = failureReasonOverrides.get(item.id);
      }

      if (rtoOrdersOverrides.has(item.id) && rtoOrdersOverrides.get(item.id) && codOverrides.has(item.id)) {
        overridden.price = minus(codOverrides.get(item.id)!, item.delivery_fee);
      }

      return overridden;
    });

    try {
      const response = await axios.patch(
        `${API_ENDPOINT}/v1/orders/receive-at-warehouse/`,
        requestData,
        config
      );
      setResponse(response);
      setMessage('Confirmed receipt at warehouse successfully');
      setPrintingWaybillsForRTO(true);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        setMessage('Failed to complete operation: ' + e.response?.statusText);
      } else {
        setMessage('Failed to complete operation');
      }
      console.log(e);
    }
    setIsLoading(false);
    setLoadingMessage('');
  };

  const handleFetchDrivers = async (query: string): Promise<DriverType[]> => {
    const endpoint = `${API_ENDPOINT}/api/v1/drivers/?page_size=10&name_or_phone=${encodeURIComponent(query)}`;

    try {
      const response = await axios.get<{
        count: number;
        results: DriverType[];
      }>(endpoint, config);

      return response.data.results.filter((driver) => !driver.is_deleted);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        setMessage('Failed to fetch driver: ' + e.response?.statusText);
      } else {
        setMessage('Failed to fetch driver');
      }
    }

    return [];
  };

  console.log('scannedOrders = ', scannedOrders);

  useEffect(()=>{
    const newCODOverrides = new Map(codOverrides)
   
    scannedOrders.forEach(item => {
      console.log("order = ", item)
      newCODOverrides.set(item.order.id, codOverrides.get(item.order.id) ?? item.order.merchant!.default_return_fee)
    })
    setCODOverrides(newCODOverrides)
  }, [scannedOrders, rtoOrdersOverrides])

  return (
    <>
      <Typography
        variant="h3"
        component="h3"
        sx={{ textAlign: 'center', mt: 0, mb: 0 }}
      >
        Receive Failed Deliveries
      </Typography>
      <br /> <br />
      {isLoading ? (
        <>
          {loadingMessage}
          <br />
          <LinearProgress />
        </>
      ) : (
        <>
          {message !== '' && (
            <Alert
              severity={
                response && response.status >= 200 && response.status < 300
                  ? 'success'
                  : 'error'
              }
            >
              <>
                {message}

                {response && response.status >= 300 && (
                  <ErrorsFlattenner rawErrors={response.data} />
                )}
              </>
            </Alert>
          )}

          <>
            <div
              style={{
                width: (isDesktop || isTablet) ? '100%' : '100%',
                textAlign: 'center',
                margin: 'auto',
              }}
            >
              <FormControl fullWidth sx={{ textAlign: 'center' }}>
                <AutocompleteWithThrottle
                  label={'Choose Driver'}
                  required={true}
                  fetch={handleFetchDrivers}
                  value={driver}
                  fetchAllOnLoad
                  getOptionLabel={(option: DriverType) =>
                    option.name + ' ' + option.phone_number
                  }
                  onChange={(e, value) => {
                    setDriver(value);
                  }}
                />
              </FormControl>

              <br />
              <br />

              <MultiPackageScanner
                key={driver?.id ?? '0'}
                label="package id"
                disableCallbackOnNothingScanned
                onScanResultChanged={(o) => setScannedOrders(o)}
                allowUndoScan={true}
                scannedOrdersTableHeader={<Alert severity='info' sx={{textAlign:"left"}}>
                  Orders scheduled for RTO will be charged based on the <b>default return fee</b> cofigured for the merchant.

                   <br/>
                  Click on the <b>Return COD</b> value below to modify it.
                  
                  </Alert>}
                validator={(
                  decodingWithVal: DecodingWithOrderVal
                ): validationResult => {
                  if (driver == null)
                    return {
                      validated: false,
                      error: 'Choose driver first.',
                    };

                  if (decodingWithVal.type !== 'permanent') {
                    return {
                      validated: false,
                      error: 'Failed orders should not have pending qr codes.',
                    };
                  }

                  if (
                    decodingWithVal.order.last_delivery_task?.driver &&
                    decodingWithVal.order.last_delivery_task?.driver.id !=
                      driver.id
                  )
                    return {
                      validated: false,
                      error: "Order doesn't belong to specified driver",
                    };

                  if (
                    decodingWithVal.order.delivery_state !=
                    DeliveryState.AWAITING_RETURN
                  )
                    return {
                      validated: false,
                      error: "Order didn't fail delivery",
                    };
                  return { validated: true };
                }}
                actionType={'Acknowledge Receipt At Warehouse'}
                getRenderOrdersProps={(
                  decodings: DecodingWithOrderVal[]
                ): GenericTableProps<DecodingWithOrderVal> => {
                  return {
                    sourceView: "failed-deliveries-receiver",
                    items: decodings,
                    fullWidth: true,
                    columns: 
                    [
                      {
                        id: 'order_id',
                        header: 'Order ID',
                        display_body: (decoding) => decoding.id,
                      },
                      {
                        id: 'customer',
                        header: 'Customer',
                        display_body: (decoding) => (
                          <>
                            {decoding.order.customer?.name}
                            <br />
                            {displayLocation(
                              decoding.order.customer_location ?? null
                            )}{' '}
                          </>
                        ),
                      },
                      {
                        id: 'merchant',
                        header: 'Merchant',
                        display_body: (decoding) => (
                          <>
                            {decoding.order.merchant?.name}
                            <br />
                            {displayLocation(
                              decoding.order.merchant_location ?? null
                            )}{' '}
                          </>
                        ),
                      },
                      {
                        id: 'delivery_status',
                        header: 'Status',
                        display_body: (decoding) =>
                          showDeliveryState(decoding.order.delivery_state),
                      },
                      {
                        id: 'workflow',
                        header: 'Workflow',
                        display_body: (decoding) =>
                          showWorkflow(
                            decoding.order.workflow,
                            decoding.order.is_exchange,
                            decoding.order.exchange_for,
                            decoding.order.return_reason
                          ),
                      },
                      {
                        id: 'failure_reason',
                        header: 'Failure Reason',
                        display_body: (decoding) => {
                          return (
                            <Box sx={{ display: 'inline-block' }}>
                              <Chip
                                label={
                                  (failureReasonOverrides.get(
                                    decoding.order.id
                                  ) ||
                                    decoding.order.last_delivery_task
                                      ?.failure_reason) ??
                                  'No Reason'
                                }
                                onClick={() =>
                                  setUpdateFailureReasonFor(decoding.order)
                                }
                                sx={{
                                  backgroundColor: '#e0e0e0',
                                  margin: 0.5, // Adjust chip margin
                                }}
                              />
                            </Box>
                          );
                        },
                      },
                      {
                        id: 'failure_commission',
                        header: 'Driver Commission',
                        hidden: !user_is_admin(user),
                        display_body: (decoding) => {
                          return (
                            <Box sx={{ display: 'inline-block' }}>
                                <Box
                                  onClick={() =>
                                    setUpdateCommissionFor(decoding.order)
                                  }
                                  sx={{
                                    display: 'inline-block',
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 1,
                                    bgcolor: 'grey.100',
                                    color: 'text.primary',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s',
                                    '&:hover': {
                                      bgcolor: 'grey.200',
                                    },
                                  }}
                                >
                                  {displayMoney(
                                    commissionOverrides.has(decoding.order.id)
                                      ? commissionOverrides.get(decoding.order.id)!
                                      : decoding.order.last_delivery_task!.failed_delivery_commission
                                  )}
                                </Box>
                            </Box>
                          );
                        },
                      },
                      {
                        id: 'schedule for rto',
                        header: 'Schedule Shop RTO',
                        display_body: (decoding) => {
                          return (
                            <Box sx={{ display: 'inline-block' }}>
                              <Checkbox
                                checked={
                                  rtoOrdersOverrides.has(decoding.order.id)
                                    ? rtoOrdersOverrides.get(decoding.order.id)
                                    : decoding.order.workflow ===
                                      Workflow.RETURN
                                }
                                onChange={(e) => {
                                  const wasRTO = rtoOrdersOverrides.has(
                                    decoding.order.id
                                  )
                                    ? rtoOrdersOverrides.get(decoding.order.id)
                                    : decoding.order.workflow ===
                                      Workflow.RETURN;
                                  const shouldRTO = !wasRTO;
                                  const newRTOOverrides = new Map(
                                    rtoOrdersOverrides
                                  );
                                  newRTOOverrides.set(
                                    decoding.order.id,
                                    shouldRTO
                                  );
                                  if (!shouldRTO) {
                                    const newCODOverrides = new Map(
                                      codOverrides
                                    );
                                    newCODOverrides.delete(decoding.order.id);
                                    setCODOverrides(newCODOverrides);
                                  }
                                  setRtoOrdersOverrides(newRTOOverrides);
                                }}
                              />
                            </Box>
                          );
                        },
                      },
                      {
                        id: 'Return COD',
                        header: 'Return COD',
                        display_body: (decoding) => {
                          return (
                            <Box sx={{ display: 'inline-block' }}>
                              {/** We should require COD only if the order is scheduled for RTO */}
                              {(rtoOrdersOverrides.get(decoding.order.id) ||
                                (!rtoOrdersOverrides.has(decoding.order.id) &&
                                  decoding.order.workflow ===
                                    Workflow.RETURN)) && (
                                <Box
                                  onClick={() =>
                                    setUpdateCODFor(decoding.order)
                                  }
                                  sx={{
                                    display: 'inline-block',
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 1,
                                    bgcolor: 'grey.100',
                                    color: 'text.primary',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s',
                                    '&:hover': {
                                      bgcolor: 'grey.200',
                                    },
                                  }}
                                >
                                  {displayMoney(
                                    codOverrides.has(decoding.order.id)
                                      ? codOverrides.get(decoding.order.id)!
                                      : get_cod(decoding.order)
                                  )}
                                </Box>
                              )}
                            </Box>
                          );
                        },
                      },
                    ],
                    getItemUID: (decoding) => generateUID(decoding),
                    selectedItems: new Map(),
                    isLoading: false,
                  };
                }}
                callback={handleSubmit}
              />
            </div>
          </>
        </>
      )}
      {updateCODFor && (
        <UpdateCOD
          order={updateCODFor}
          onSubmit={(m) => {
            const newCODOverrides = new Map(codOverrides);
            newCODOverrides.set(updateCODFor.id, m);
            setCODOverrides(newCODOverrides);
          }}
          onClose={() => setUpdateCODFor(null)}
          defaultCOD={
            codOverrides.has(updateCODFor.id)
              ? codOverrides.get(updateCODFor.id)!
              : get_cod(updateCODFor)
          }
        />
      )}
      {updateCommissionFor && (
        <UpdateCommissionDialog 
          // should update commission as a failing task
          task={{...updateCommissionFor.last_delivery_task!, status: OrderDeliveryTaskState.FAILED, failed_delivery_commission: commissionOverrides.get(updateCommissionFor.id) ?? updateCommissionFor.last_delivery_task!.failed_delivery_commission}} 
          updateDeliveryTaskCommission={function (m: Money[]): void {
            const newCommissionOverrides = new Map(commissionOverrides);
            newCommissionOverrides.set(updateCommissionFor.id, m);
            setCommissionOverrides(newCommissionOverrides);
          } } 
          onClose={()=>setUpdateCommissionFor(null)} />
       
      )}
      {updateFailureReasonFor && (
        <CustomDialog open={updateFailureReasonFor != null}>
          <SetFailureReason
            failure_reasons={[
              'Shop closed',
              'Wrong amount',
              'Wrong invoice',
              'Person absent',
              'Invoice lost',
            ]}
            onSubmit={(r) => {
              const newOverrides = new Map(failureReasonOverrides);
              newOverrides.set(updateFailureReasonFor.id, r);
              setFailureReasonOverrides(newOverrides);
            }}
            onClose={() => setUpdateFailureReasonFor(null)}
          />
        </CustomDialog>
      )}
      {printingWaybillsForRTO && (
        <CustomDialog
          open={printingWaybillsForRTO}
          onClose={() => setPrintingWaybillsForRTO(false)}
          maxWidth="md"
        >
          <div
            style={{
              textAlign: 'right',
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <DialogTitle>Print Packages Waybills </DialogTitle>
            <IconButton onClick={() => setPrintingWaybillsForRTO(false)}>
              <ClearIcon color="primary" fontSize="large" />
            </IconButton>
          </div>

          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {
              <PrintWaybillsMultiSizes
                onClose={() => setPrintingWaybillsForRTO(false)}
                orders={scannedOrders
                  .filter(
                    (item) =>
                      (item.order.workflow === Workflow.RETURN &&
                        !rtoOrdersOverrides.has(item.order.id)) ||
                      rtoOrdersOverrides.get(item.order.id)
                  )
                  .map((item) => item.order)
                  .map((item) => {
                    return {
                      ...item,
                      workflow: Workflow.RETURN,
                      price: codOverrides.has(item.id)
                        ? minus(codOverrides.get(item.id)!, item.delivery_fee)
                        : item.price,
                    };
                  })}
              />
            }
          </div>
        </CustomDialog>
      )}
    </>
  );
}

export default FailedDeliveriesReceiver;
